import { AnimationClip, type KeyframeTrack, QuaternionKeyframeTrack, VectorKeyframeTrack } from 'three'
import { type PoseSnapshot } from './PoseSnapshot'

/**
 * Builds and edits `AnimationClip`s from pose snapshots — the engine behind a
 * pose-and-bake authoring workflow.
 *
 * Mental model:
 *   - one clip is "the clip we are currently editing"
 *   - the user poses the skeleton in the viewport, scrubs the playhead to time `t`,
 *     clicks "Set Keyframe"
 *   - we capture a `PoseSnapshot` and call `upsert_pose_keyframe(clip, snapshot)`
 *   - the clip ends up with one quaternion keyframe per bone at time `t`
 *
 * Tracks are stored using Three.js's standard `<bone>.<property>` naming so the
 * resulting clip is interchangeable with the existing animation pipeline (export,
 * playback, retargeting) with no special handling.
 *
 * All operations are non-mutating: every method returns a NEW `AnimationClip` and
 * leaves the input untouched. This keeps undo/redo trivial — the caller just stores
 * a stack of clip references.
 */
export class KeyframeBuilder {
  private static readonly QUAT_STRIDE = 4
  private static readonly POS_STRIDE = 3
  private static readonly TIME_EPSILON = 1e-4

  /** Returns a new empty `AnimationClip` with the given name. */
  static create_empty_clip (name: string): AnimationClip {
    return new AnimationClip(name, 0, [])
  }

  /**
   * Inserts (or replaces, if an existing keyframe is within `TIME_EPSILON` of `snapshot.time`)
   * one rotation keyframe per bone in the snapshot, plus position keyframes for any bones
   * whose snapshot includes them. Returns a new clip.
   */
  static upsert_pose_keyframe (clip: AnimationClip, snapshot: PoseSnapshot): AnimationClip {
    const time = snapshot.time

    // Map every existing track by name for fast lookup. We rebuild the tracks list at the end.
    const tracks_by_name = new Map<string, KeyframeTrack>()
    for (const track of clip.tracks) {
      tracks_by_name.set(track.name, track)
    }

    for (const bone_snap of snapshot.bones) {
      const quat_name = `${bone_snap.bone_name}.quaternion`
      tracks_by_name.set(
        quat_name,
        this.upsert_quaternion_keyframe(tracks_by_name.get(quat_name) as QuaternionKeyframeTrack | undefined, quat_name, time, bone_snap.quaternion)
      )

      if (bone_snap.position !== undefined) {
        const pos_name = `${bone_snap.bone_name}.position`
        tracks_by_name.set(
          pos_name,
          this.upsert_vector_keyframe(tracks_by_name.get(pos_name) as VectorKeyframeTrack | undefined, pos_name, time, bone_snap.position, this.POS_STRIDE)
        )
      }
    }

    // Preserve a user-set duration that's larger than the computed track-based one.
    // Empty/sparse user clips ship with a nominal duration (e.g. 2 seconds) so the
    // scrubber has range; baking a single keyframe at t=0 would otherwise collapse
    // the clip back to duration 0.
    const computed_duration = this.compute_duration(tracks_by_name)
    const new_duration = Math.max(computed_duration, clip.duration)
    return new AnimationClip(clip.name, new_duration, Array.from(tracks_by_name.values()))
  }

  /**
   * Removes all keyframes (across every track) at time `t`. Tracks that become empty
   * are removed entirely. Returns a new clip.
   */
  static remove_keyframe_at_time (clip: AnimationClip, time: number): AnimationClip {
    const new_tracks: KeyframeTrack[] = []
    for (const track of clip.tracks) {
      const keep_indices: number[] = []
      for (let i = 0; i < track.times.length; i++) {
        if (Math.abs(track.times[i] - time) > this.TIME_EPSILON) {
          keep_indices.push(i)
        }
      }
      if (keep_indices.length === 0) continue

      const stride = this.stride_for(track)
      const new_times = new Float32Array(keep_indices.length)
      const new_values = new Float32Array(keep_indices.length * stride)
      for (let i = 0; i < keep_indices.length; i++) {
        const src = keep_indices[i]
        new_times[i] = track.times[src]
        for (let s = 0; s < stride; s++) {
          new_values[i * stride + s] = track.values[src * stride + s]
        }
      }
      new_tracks.push(this.clone_track_with(track, new_times, new_values))
    }

    const tracks_by_name = new Map<string, KeyframeTrack>()
    for (const track of new_tracks) tracks_by_name.set(track.name, track)
    const computed_duration = this.compute_duration(tracks_by_name)
    const new_duration = Math.max(computed_duration, clip.duration)
    return new AnimationClip(clip.name, new_duration, new_tracks)
  }

  /**
   * Returns the sorted list of unique keyframe times across every track in the clip.
   * Two times within `TIME_EPSILON` are deduplicated.
   */
  static collect_keyframe_times (clip: AnimationClip): number[] {
    const all_times: number[] = []
    for (const track of clip.tracks) {
      for (let i = 0; i < track.times.length; i++) all_times.push(track.times[i])
    }
    all_times.sort((a, b) => a - b)
    const unique: number[] = []
    for (const t of all_times) {
      if (unique.length === 0 || Math.abs(unique[unique.length - 1] - t) > this.TIME_EPSILON) {
        unique.push(t)
      }
    }
    return unique
  }

  /** Returns a new clip with a different name. The tracks and duration are preserved by reference. */
  static rename_clip (clip: AnimationClip, new_name: string): AnimationClip {
    return new AnimationClip(new_name, clip.duration, clip.tracks)
  }

  // -- Internal helpers --------------------------------------------------------

  private static upsert_quaternion_keyframe (
    existing: QuaternionKeyframeTrack | undefined,
    track_name: string,
    time: number,
    quat: [number, number, number, number]
  ): QuaternionKeyframeTrack {
    const stride = this.QUAT_STRIDE
    const { times, values } = this.upsert_keyframe_data(existing, time, quat, stride)
    return new QuaternionKeyframeTrack(track_name, Array.from(times), Array.from(values))
  }

  private static upsert_vector_keyframe (
    existing: VectorKeyframeTrack | undefined,
    track_name: string,
    time: number,
    vec: [number, number, number],
    stride: number
  ): VectorKeyframeTrack {
    const { times, values } = this.upsert_keyframe_data(existing, time, vec, stride)
    return new VectorKeyframeTrack(track_name, Array.from(times), Array.from(values))
  }

  private static upsert_keyframe_data (
    existing: KeyframeTrack | undefined,
    time: number,
    new_value: number[],
    stride: number
  ): { times: Float32Array, values: Float32Array } {
    const old_times = existing !== undefined ? Array.from(existing.times) : []
    const old_values = existing !== undefined ? Array.from(existing.values) : []

    // Find the index of an existing keyframe at this time, or where to insert.
    let replace_at = -1
    let insert_at = old_times.length
    for (let i = 0; i < old_times.length; i++) {
      if (Math.abs(old_times[i] - time) < this.TIME_EPSILON) {
        replace_at = i
        break
      }
      if (old_times[i] > time) {
        insert_at = i
        break
      }
    }

    if (replace_at !== -1) {
      // Replace existing keyframe values in place.
      const new_times = new Float32Array(old_times)
      const new_values = new Float32Array(old_values)
      for (let s = 0; s < stride; s++) {
        new_values[replace_at * stride + s] = new_value[s]
      }
      return { times: new_times, values: new_values }
    }

    // Insert a new keyframe at `insert_at`, shifting the rest right.
    const out_times = new Float32Array(old_times.length + 1)
    const out_values = new Float32Array(old_values.length + stride)

    for (let i = 0; i < insert_at; i++) {
      out_times[i] = old_times[i]
      for (let s = 0; s < stride; s++) {
        out_values[i * stride + s] = old_values[i * stride + s]
      }
    }
    out_times[insert_at] = time
    for (let s = 0; s < stride; s++) {
      out_values[insert_at * stride + s] = new_value[s]
    }
    for (let i = insert_at; i < old_times.length; i++) {
      out_times[i + 1] = old_times[i]
      for (let s = 0; s < stride; s++) {
        out_values[(i + 1) * stride + s] = old_values[i * stride + s]
      }
    }

    return { times: out_times, values: out_values }
  }

  private static compute_duration (tracks_by_name: Map<string, KeyframeTrack>): number {
    let max_time = 0
    for (const track of tracks_by_name.values()) {
      const last_time = track.times[track.times.length - 1] ?? 0
      if (last_time > max_time) max_time = last_time
    }
    return max_time
  }

  private static stride_for (track: KeyframeTrack): number {
    if (track instanceof QuaternionKeyframeTrack) return this.QUAT_STRIDE
    // Vector tracks (.position, .scale) and the rare scalar tracks. Inferring from data length
    // is more robust than instanceof here because Three has several VectorKeyframeTrack-like
    // subclasses and minified bundles don't always preserve the prototype chain.
    return Math.round(track.values.length / track.times.length)
  }

  private static clone_track_with (track: KeyframeTrack, times: Float32Array, values: Float32Array): KeyframeTrack {
    if (track instanceof QuaternionKeyframeTrack) {
      return new QuaternionKeyframeTrack(track.name, Array.from(times), Array.from(values))
    }
    if (track instanceof VectorKeyframeTrack) {
      return new VectorKeyframeTrack(track.name, Array.from(times), Array.from(values))
    }
    // Fallback: clone via the original track and overwrite arrays.
    const cloned = track.clone()
    ;(cloned as { times: Float32Array }).times = times
    ;(cloned as { values: Float32Array }).values = values
    return cloned
  }
}
